import { Column, GridApi, RowNode } from "ag-grid-community";
import { safeParseFloat } from "lib/number";
import { MetricsKind } from "../types";

const buildMetricColIdSuffix = (metricKind: MetricsKind) => `metric|${metricKind}|`;
/**
 * Generate column id for metric. This is very important for consistency naming.
 * Some calculation logic will lookup other metrics by column id. Eg: Row group metrics calculation
 */
export const generateMetricColId = (colId: string, metricKind: MetricsKind) => {
    return `${colId}_${buildMetricColIdSuffix(metricKind)}`;
};

/**
 * Get the suffixed metric column id
 */
export const getMetricColIdSuffix = (colId: string) => {
    const pattern = new RegExp("(metric\\|[a-zA-Z_]+\\|)", "i");
    const matches = colId.match(pattern);
    return matches?.[1];
};

/**
 * Get the prefixed metric column id
 */
export const getMetricColIdPrefix = (colId: string) => {
    const pattern = new RegExp("([a-z0-9_]+)_metric\\|[a-zA-Z_]+\\|", "ig");
    const array = [...colId.matchAll(pattern)];
    const firstGroup = array.map((m) => m[1]);
    return firstGroup?.[0];
};

/**
 * Function to get column id without metric suffix
 */
export const getNonMetricColId = (colId: string) => {
    const metricColId = getMetricColIdSuffix(colId);

    if (!metricColId) {
        return colId;
    }
    return colId.replace(`_${metricColId}`, "");
};

/**
 * Function to rename metric column id to different metric.
 * Major use case for this is to get column id for other metric.
 */
export const renameMetricColId = (colId: string, metricKind: MetricsKind) => {
    const metricColId = getMetricColIdSuffix(colId);

    if (!metricColId) {
        return colId;
    }
    return colId.replace(metricColId, buildMetricColIdSuffix(metricKind));
};

/**
 * For grouped row, loop through children and grandchildren to get their cell values.
 */
export const getChildrenValues = (api: GridApi, node: RowNode, column: Column): number[] => {
    let values = [];
    if (node.group) {
        node.childrenAfterGroup.forEach((childNode) => {
            values = values.concat(getChildrenValues(api, childNode, column));
        });
    } else {
        values = values.concat([safeParseFloat(api.getValue(column, node), 0)]);
    }

    return values;
};

/**
 * For grouped row, loop through children and grandchildren to get their cell values.
 */
export const loopNestedChildren = (node: RowNode, callbackFn: (childNode: RowNode) => void) => {
    if (node.group) {
        node.childrenAfterFilter.forEach((childNode) => {
            loopNestedChildren(childNode, callbackFn);
        });
    } else {
        callbackFn(node);
    }
};
